import { useQuery } from "@apollo/client";
import { OpenInNew } from "@mui/icons-material";
import { Button, IconButton, Typography } from "@mui/material";
import { ReactNode, useState } from "react";
import {
  useQueryParam,
  withDefault,
  NumberParam,
  StringParam,
  createEnumParam,
} from "use-query-params";

import {
  DoNotContactRequest,
  DoNotContactRequestAction,
  DoNotContactRequestSourceType,
  ListDoNotContactRequestsQuery,
  SortOrderType,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table, { TableBox, TableControls } from "~frontend/components/Table";
import { LIST_DO_NOT_CONTACT_REQUESTS } from "~frontend/graphql/doNotContactRequests";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";

import { AddContactModal } from "./AddContactModal";
import { ContactDetailsModal } from "./ContactDetailsModal";

export type StaffRequest = Omit<DoNotContactRequest, "sourceType">;

export const DoNotContactList = (): ReactNode => {
  useBreadcrumbs([
    {
      label: "Do Not Contact List",
      path: "/staff/do-not-contact-list",
    },
  ]);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 25)
  );
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [selectedRequest, setSelectedContact] = useState<StaffRequest>();

  const [sortOrder, setSortOrder] = useQueryParam(
    "sortOrder",
    withDefault(StringParam, SortOrderType.Desc)
  );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const handleSetSearch = (val: string) => {
    setSearch(val);
    setPage(0);
  };

  const ActionParam = createEnumParam<DoNotContactRequestAction>([
    DoNotContactRequestAction.Add,
    DoNotContactRequestAction.Remove,
  ]);

  const [action, setAction] = useQueryParam<DoNotContactRequestAction>(
    "action",
    withDefault(ActionParam, DoNotContactRequestAction.Add)
  );

  const handleSetAction = (val: DoNotContactRequestAction) => {
    setAction(val);
    setPage(0);
  };

  const showDnc = action === DoNotContactRequestAction.Add;

  const { loading, error, data } = useQuery<ListDoNotContactRequestsQuery>(
    LIST_DO_NOT_CONTACT_REQUESTS,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        input: {
          limit: pageSize,
          offset: page * pageSize,
          sourceTypes: [DoNotContactRequestSourceType.StaffManual],
          sortOrder: sortOrder,
          search: search || undefined,
          action: action,
        },
      },
    }
  );

  return (
    <PageContainer maxWidth="xl">
      <FlexBox direction="column" gap={2}>
        <PageHeader>
          Do Not Contact List
          <Typography>
            Numbers added here will not be contacted from Scale to Win Text,
            Scale to Win Dialer, or Spoke.
          </Typography>
        </PageHeader>
        <AsyncWrapper loading={[loading]} errors={[error]} data={data}>
          {({ listDoNotContactRequests }) => {
            return (
              <>
                <TableControls
                  addButtons={[
                    {
                      onClick: () => setShowAddContactModal(true),
                      id: "add-contact",
                      label: "Add contact",
                    },
                  ]}
                  search={{
                    value: search,
                    onChange: handleSetSearch,
                    placeholder: "Search for a phone number, name, or email",
                  }}
                  sort={{
                    value: sortOrder,
                    onChange: setSortOrder,
                    options: [
                      {
                        value: SortOrderType.Desc,
                        label: "Created at, Newest First",
                      },
                      {
                        value: SortOrderType.Asc,
                        label: "Created at, Oldest First",
                      },
                    ],
                  }}
                />
                <AddContactModal
                  open={showAddContactModal}
                  onClose={() => setShowAddContactModal(false)}
                />
                <FlexBox justifyContent="end">
                  <Button
                    variant={showDnc ? "outlined" : "contained"}
                    color="secondary"
                    size="small"
                    onClick={() => {
                      handleSetAction(
                        showDnc
                          ? DoNotContactRequestAction.Remove
                          : DoNotContactRequestAction.Add
                      );
                    }}
                    sx={{ width: "fit-content", height: "27px" }}
                  >
                    {showDnc ? "Show archived" : "Show do not contact list"}
                  </Button>
                </FlexBox>

                <TableBox>
                  <Table<StaffRequest>
                    containerSx={{
                      ".MuiTableCell-root": {
                        verticalAlign: "top",
                        position: "relative",
                      },
                    }}
                    columns={[
                      {
                        key: "contact",
                        title: "Contact",
                        sx: { width: "18em" },
                        align: "left",
                        renderValue: row => (
                          <FlexBox direction="column">
                            {row.phoneNumber}
                            <FlexBox direction="column">
                              <Typography variant="body3">
                                {row.contactName}
                              </Typography>
                              <Typography variant="body3">
                                {row.contactEmail}
                              </Typography>
                            </FlexBox>
                          </FlexBox>
                        ),
                      },
                      {
                        key: showDnc ? "added_at" : "removed_at",
                        title: showDnc ? "Added At" : "Removed At",
                        sx: { width: "18em" },
                        align: "left",
                        renderValue: row =>
                          new Date(row.createdAt).toLocaleString(),
                      },
                      {
                        key: showDnc ? "added_by" : "removed_by",
                        title: showDnc ? "Added By" : "Removed By",
                        sx: { width: "18em" },
                        align: "left",
                        renderValue: row => row.createdByStaffEmail,
                      },
                      {
                        key: "details",
                        title: "Details",
                        align: "right",
                        renderValue: row => (
                          <IconButton
                            onClick={() => setSelectedContact(row)}
                            sx={{
                              position: "absolute",
                              right: "2px",
                              top: "7px",
                            }}
                            title="View contact details"
                          >
                            <OpenInNew sx={{ fontSize: "1.2rem" }} />
                          </IconButton>
                        ),
                      },
                    ]}
                    rows={listDoNotContactRequests.records}
                    emptyState="No requests found"
                    totalCount={listDoNotContactRequests.totalCount}
                    rowsPerPage={pageSize}
                    onChangeRowsPerPage={newPageSize =>
                      setPageSize(newPageSize)
                    }
                    page={page}
                    onChangePage={(page: number) => setPage(page)}
                  ></Table>
                  <ContactDetailsModal
                    selectedRequest={selectedRequest}
                    onClose={() => setSelectedContact(undefined)}
                  />
                </TableBox>
              </>
            );
          }}
        </AsyncWrapper>
      </FlexBox>
    </PageContainer>
  );
};
